import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Search, Calendar, FileText } from 'lucide-react';
import { newsletters, categories } from '../data/mock';

const NewsletterLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredNewsletters = useMemo(() => {
    return newsletters.filter((newsletter) => {
      const matchesSearch = 
        newsletter.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        newsletter.summary.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = 
        selectedCategory === 'All' || newsletter.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short'
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Knowledge Library</span>
          </div>
          
          <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl text-foreground mb-6">
            Newsletter Library
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl">
            Practical guidance for business owners preparing to sell, grow, or exit.
          </p>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="bg-card border-b border-border sticky top-28 lg:top-32 z-40 shadow-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search Bar */}
            <div className="relative max-w-md w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search newsletters..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 search-input h-12 w-full rounded-xl text-foreground"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-smooth ${
                    selectedCategory === category
                      ? 'btn-primary'
                      : 'bg-secondary text-muted-foreground hover:text-foreground hover:bg-accent'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Listings - Grid Layout */}
      <section className="bg-background relative">
        <div className="absolute inset-0 pattern-bg opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-10 lg:py-14">
          {filteredNewsletters.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-lg mb-2">
                No newsletters found matching your search.
              </p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search terms or category filter.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredNewsletters.map((newsletter) => (
                <Link
                  key={newsletter.id}
                  to={`/newsletter/${newsletter.id}`}
                  className="feature-card rounded-2xl overflow-hidden group"
                >
                  {/* Image */}
                  <div className="newsletter-image h-48 overflow-hidden">
                    <img 
                      src={newsletter.image} 
                      alt={newsletter.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="category-badge">
                        {newsletter.category}
                      </span>
                      <span className="flex items-center text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(newsletter.date)}
                      </span>
                    </div>
                    
                    <h3 className="font-display text-lg text-foreground mb-2 group-hover:text-primary transition-smooth line-clamp-2">
                      {newsletter.title}
                    </h3>
                    
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                      {newsletter.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsletterLibrary;
